/**
 * ValueStep
 *
 * ROI Calculator / Value Proposition - redesigned with world-class
 * editorial aesthetics. Inspired by Collins, Pentagram.
 *
 * Design Philosophy:
 * - Clear value communication without feeling sales-y
 * - Elegant pricing cards with refined interactions
 * - Subtle motion that enhances understanding
 * - Premium feel throughout
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '../../../lib/cn';
import {
  Calculator,
  DollarSign,
  TrendingUp,
  Clock,
  Check,
  ChevronRight,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingHeading,
  OnboardingButton,
} from '../OnboardingLayout';
import type { OnboardingStepProps } from '../OnboardingRouter';

// Pricing plans for display
const PLANS = [
  {
    name: 'Starter',
    price: 29,
    period: 'month',
    images: 10,
    features: ['10 staged images/month', 'All design styles', 'High-res downloads', 'Email support'],
    highlight: false,
  },
  {
    name: 'Professional',
    price: 79,
    period: 'month',
    images: 50,
    features: [
      '50 staged images/month',
      'All design styles',
      'High-res downloads',
      'Priority support',
      'Team collaboration',
    ],
    highlight: true,
  },
  {
    name: 'Business',
    price: 199,
    period: 'month',
    images: 200,
    features: [
      '200 staged images/month',
      'All design styles',
      'High-res downloads',
      'Dedicated support',
      'API access',
      'Custom branding',
    ],
    highlight: false,
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function ValueStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>('Professional');

  // Get monthly volume from onboarding data for ROI calculation
  const monthlyVolume = onboardingData.monthly_property_volume;
  const volumeNumber = useMemo(() => {
    switch (monthlyVolume) {
      case '1-3':
        return 2;
      case '4-10':
        return 7;
      case '11-25':
        return 18;
      case '26+':
        return 30;
      default:
        return 5;
    }
  }, [monthlyVolume]);

  // Calculate savings
  const traditionalCostPerRoom = 500;
  const roomsPerProperty = 3;
  const traditionalMonthlyCost = volumeNumber * roomsPerProperty * traditionalCostPerRoom;
  const selectedPlanData = PLANS.find((p) => p.name === selectedPlan);
  const virtualStagingCost = selectedPlanData?.price || 79;
  const monthlySavings = traditionalMonthlyCost - virtualStagingCost;
  const yearlySavings = monthlySavings * 12;

  const handleSelectPlan = (planName: string) => {
    // Track plan selection
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'onboarding_plan_selected',
        plan: planName,
        session_id: onboardingData.session_id,
      });
    }

    // Navigate to pricing/checkout
    navigate(`/pricing?plan=${planName.toLowerCase()}&source=onboarding`);
  };

  const handleContinueFree = async () => {
    // Track analytics
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'onboarding_continue_free',
        session_id: onboardingData.session_id,
      });
    }

    // Complete onboarding and go to dashboard
    await onStepComplete('value', {
      chose_free_plan: true,
    });
  };

  return (
    <OnboardingLayout showProgress={true} showSkip={true}>
      <OnboardingStepContainer maxWidth="4xl">
        <OnboardingHeading
          eyebrow="Your investment"
          title="Unlock unlimited potential"
          subtitle="See how much you could save with VirtualStagePro"
        />

        {/* ROI Calculator Summary */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="bg-white border border-stone/20 rounded-3xl p-6 sm:p-8 mb-8 shadow-soft"
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="size-10 rounded-xl bg-olive/10 flex items-center justify-center">
              <Calculator className="size-5 text-olive" />
            </div>
            <h3 className="font-sans font-semibold text-charcoal">Your Potential Savings</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {/* Traditional cost */}
            <div className="text-center p-4 rounded-2xl bg-stone/10">
              <p className="text-sand text-sm font-sans mb-2 uppercase tracking-wider">
                Traditional
              </p>
              <p className="text-2xl font-serif font-semibold text-charcoal">
                ${traditionalMonthlyCost.toLocaleString()}
                <span className="text-sm text-sand font-sans">/mo</span>
              </p>
              <p className="text-xs text-stone mt-1">
                {volumeNumber} properties × {roomsPerProperty} rooms
              </p>
            </div>

            {/* Virtual staging cost */}
            <div className="text-center p-4 rounded-2xl bg-olive/10">
              <p className="text-olive text-sm font-sans mb-2 uppercase tracking-wider">
                VirtualStagePro
              </p>
              <p className="text-2xl font-serif font-semibold text-olive">
                ${virtualStagingCost}
                <span className="text-sm text-olive/70 font-sans">/mo</span>
              </p>
              <p className="text-xs text-olive/70 mt-1">Unlimited rooms included</p>
            </div>

            {/* Savings */}
            <div className="text-center p-4 rounded-2xl bg-charcoal text-alabaster">
              <p className="text-alabaster/70 text-sm font-sans mb-2 uppercase tracking-wider">
                You Save
              </p>
              <p className="text-3xl font-serif font-bold text-alabaster">
                ${monthlySavings.toLocaleString()}
              </p>
              <p className="text-sm text-olive mt-1 font-semibold">
                ${yearlySavings.toLocaleString()}/year
              </p>
            </div>
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10"
        >
          {[
            {
              icon: DollarSign,
              title: 'Save Thousands',
              desc: '90%+ savings vs traditional',
            },
            {
              icon: Clock,
              title: 'Ready in Minutes',
              desc: 'No scheduling needed',
            },
            {
              icon: TrendingUp,
              title: 'Sell Faster',
              desc: 'Staged homes sell 73% faster',
            },
          ].map((benefit) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                variants={itemVariants}
                className="flex items-start gap-3 p-5 bg-white rounded-2xl border border-stone/20"
              >
                <div className="size-10 bg-olive/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="size-5 text-olive" />
                </div>
                <div>
                  <h4 className="font-sans font-semibold text-charcoal">{benefit.title}</h4>
                  <p className="text-sm text-sand">{benefit.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Pricing Plans */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mb-10"
        >
          <h3 className="text-center font-serif text-xl text-charcoal mb-8">Choose your plan</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((plan, index) => {
              const isSelected = selectedPlan === plan.name;

              return (
                <motion.button
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  onClick={() => setSelectedPlan(plan.name)}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'relative p-6 rounded-2xl text-left transition-all duration-300',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                    isSelected
                      ? 'bg-charcoal shadow-lift'
                      : 'bg-white border border-stone/30 hover:border-stone/50 hover:shadow-soft',
                    plan.highlight && !isSelected && 'ring-2 ring-olive/20'
                  )}
                >
                  {/* Popular badge */}
                  {plan.highlight && (
                    <div
                      className={cn(
                        'absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-sans font-medium rounded-full',
                        isSelected ? 'bg-olive text-white' : 'bg-olive text-white'
                      )}
                    >
                      Most Popular
                    </div>
                  )}

                  <h4
                    className={cn(
                      'font-sans font-semibold text-lg mb-3',
                      isSelected ? 'text-alabaster' : 'text-charcoal'
                    )}
                  >
                    {plan.name}
                  </h4>

                  <div className="mb-5">
                    <span
                      className={cn(
                        'text-4xl font-serif font-bold',
                        isSelected ? 'text-alabaster' : 'text-charcoal'
                      )}
                    >
                      ${plan.price}
                    </span>
                    <span
                      className={cn(
                        'text-sm',
                        isSelected ? 'text-alabaster/70' : 'text-sand'
                      )}
                    >
                      /{plan.period}
                    </span>
                  </div>

                  <ul className="space-y-2.5">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className={cn(
                          'flex items-start gap-2 text-sm',
                          isSelected ? 'text-alabaster/80' : 'text-sand'
                        )}
                      >
                        <Check
                          className={cn(
                            'size-4 flex-shrink-0 mt-0.5',
                            isSelected ? 'text-olive' : 'text-olive'
                          )}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Selection indicator */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                      className="absolute top-4 right-4 size-6 bg-olive rounded-full flex items-center justify-center"
                    >
                      <Check className="size-4 text-white" />
                    </motion.div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="flex flex-col items-center gap-4"
        >
          <OnboardingButton
            onClick={() => selectedPlan && handleSelectPlan(selectedPlan)}
            disabled={!selectedPlan}
            size="lg"
          >
            <Sparkles className="size-5" />
            Start with {selectedPlan}
            <ArrowRight className="size-5" />
          </OnboardingButton>

          <button
            onClick={handleContinueFree}
            className="text-sand hover:text-charcoal text-sm font-sans transition-colors duration-200"
          >
            Continue with free trial instead
          </button>
        </motion.div>

        {/* Money-back guarantee */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-xs text-stone font-sans mt-8"
        >
          30-day money-back guarantee · Cancel anytime · No long-term contracts
        </motion.p>
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
